import { broadcastUpdatedDocumentation } from 'serviceWorker/broadcasts/documentations';
import * as documentations from 'serviceWorker/db/documentations/documentations';
import { get as getCurrentProject } from 'serviceWorker/db/selectedProject';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import {
  isExportAsVersionDto,
  type ExportDocumentOutDto,
  type ExportToNewDocumentationDto,
} from 'shared/domain/issue/mapping/exportDocument/toOut/createExportDocumentOutDto';
import { Fetcher } from 'serviceWorker/services/httpQueue/fetcher';
import type { ExportToDocumentationResultData } from 'shared/domain/issue/linkedDocumentation/operations';
import { ImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { DocumentationInDto } from 'shared/dtos/in/documentation';
import { ImageRotation } from 'shared/dtos/out/document';
import { UploadStatus } from 'shared/types/uploadStatus';
import { nowISO } from 'shared/utils/date/dates';
import { resolveExtensionFromFileName } from 'shared/utils/fileExtensions';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import { debugLog } from 'shared/logger/debugLog';
import { scopeOfProject } from 'serviceWorker/helpers/dataScope';

type UpsertExportedDocumentationOptions = {
  imageRotation?: number;
  preferredExtension?: string;
};

function extractDocumentationInDto(
  response: unknown
): DocumentationInDto | null {
  if (!response || typeof response !== 'object') {
    return null;
  }

  const record = response as Record<string, unknown>;

  if (typeof record._id === 'string' && Array.isArray(record.versions)) {
    return record as unknown as DocumentationInDto;
  }

  for (const key of ['documentation', 'item', 'data'] as const) {
    const nested = record[key];
    if (!nested || typeof nested !== 'object') {
      continue;
    }

    const nestedRecord = nested as Record<string, unknown>;
    if (
      typeof nestedRecord._id === 'string' &&
      Array.isArray(nestedRecord.versions)
    ) {
      return nestedRecord as unknown as DocumentationInDto;
    }
  }

  return null;
}

function buildFallbackDocumentationInDto(
  data: ExportToDocumentationResultData,
  payload: ExportToNewDocumentationDto,
  options?: UpsertExportedDocumentationOptions
): DocumentationInDto {
  const now = nowISO();
  const fileName = data.documentationName ?? payload.name;
  const extension =
    options?.preferredExtension?.replace(/^\./, '') ||
    resolveExtensionFromFileName(fileName) ||
    EXTENSIONS.PDF;

  return {
    _id: data.documentationId,
    name: fileName,
    number: payload.number ?? '',
    parentId: payload.isParentRootDirectory ? undefined : payload.parentId,
    deleted: false,
    createdAt: now,
    modifiedAt: now,
    // The backend creates the documentation without a current version; the export sets it afterwards.
    currentVersionId: null,
    versions: [
      {
        _id: data.versionId,
        deleted: false,
        createdAt: now,
        modifiedAt: now,
        uploadStatus: UploadStatus.success,
        extension,
        remoteVersionId: '',
        imageConvertStatus: ImageConvertStatus.SUCCESS,
        name: fileName,
        number: 1,
        imageRotation:
          typeof options?.imageRotation === 'number'
            ? (options.imageRotation as ImageRotation)
            : ImageRotation.ROTATE_0,
      },
    ],
  };
}

async function persistDocumentationDto(
  documentation: DocumentationInDto,
  projectId: string
): Promise<void> {
  await scopeOfProject(projectId).write(async () => {
    await documentations.updateBatch([documentation]);
    documentationFilteringCacheManager.invalidateDocumentationFilteringCachesByParentIds(
      [documentation.parentId ?? ROOT_DIR_ID]
    );
    broadcastUpdatedDocumentation({
      _id: documentation._id,
      parentId: documentation.parentId,
    });
  });
}

export async function upsertExportedDocumentationAfterExport(
  data: ExportToDocumentationResultData,
  payload: ExportDocumentOutDto,
  options?: UpsertExportedDocumentationOptions
): Promise<void> {
  const project = await getCurrentProject();
  if (!project?._id || !data.documentationId) {
    return;
  }

  try {
    const response = await Fetcher.GET(
      `/project/${project._id}/documentation/${data.documentationId}`
    );
    const documentation = extractDocumentationInDto(response);

    if (documentation?._id) {
      await persistDocumentationDto(documentation, project._id);
      return;
    }
  } catch (error) {
    debugLog('[upsertExportedDocumentationAfterExport] GET failed', {
      documentationId: data.documentationId,
      error,
    });
  }

  // The documentation exists on this device already, with versions this record would drop: the
  // sync that follows the export brings the new one.
  if (isExportAsVersionDto(payload)) {
    return;
  }

  const fallbackDocumentation = buildFallbackDocumentationInDto(
    data,
    payload,
    options
  );
  await persistDocumentationDto(fallbackDocumentation, project._id);
}
